import { ApiResponse, PaginatedApiResponse } from '../shared.types';

import {
  VoucherDetails,
  CreateVoucherBatchFormData,
  CreateComplimentaryVoucherFormData,
  PaymentPayload,
  VoucherBatch,
  UpdateVoucherValidityResponse,
} from '@/api/voucher/voucher.types.ts';
import axiosServer from '@/services/axios';
import {
  convertDateToUnix,
  getEndOfDay,
  getStartOfDay,
} from '@/utils/dateUtil.ts';
import { transformToApiError } from '@/utils/error.ts';

const voucherEndpoint = '/voucher';
const batchEndpoint = '/voucher/batch';

export const createVoucherBatch = async (
  data: CreateVoucherBatchFormData
): Promise<ApiResponse<{ vouchers: VoucherDetails[] }>> => {
  try {
    const postData = {
      ...data,
      golfCourseIds: [data.golfCourseId],
      validFromUnix: convertDateToUnix(new Date(getStartOfDay(data.validFor))),
      validUntilUnix: convertDateToUnix(new Date(getEndOfDay(data.validFor))),
    };

    const response = await axiosServer.post(`${batchEndpoint}`, postData);
    return response.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const updateVoucherValidity = async (
  voucherBatchId: string,
  validFor: string
): Promise<ApiResponse<{ voucherBatch: UpdateVoucherValidityResponse }>> => {
  try {
    const postData = {
      validFromUnix: convertDateToUnix(new Date(getStartOfDay(validFor))),
      validUntilUnix: convertDateToUnix(new Date(getEndOfDay(validFor))),
    };

    const response = await axiosServer.patch(
      `${batchEndpoint}/${voucherBatchId}/validity`,
      postData
    );
    return response.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const getVoucherBatchesSummary = async (
  page: number,
  pageSize: number,
  searchText?: string
): Promise<PaginatedApiResponse<VoucherBatch>> => {
  try {
    const params: Record<string, any> = { page, pageSize };
    if (searchText) {
      params.searchText = searchText;
    }

    const response = await axiosServer.get(`${batchEndpoint}`, { params });
    return response.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const getVoucherDetailsById = async (
  id: string
): Promise<ApiResponse<{ voucherDetails: VoucherDetails }>> => {
  try {
    const response = await axiosServer.get(`${voucherEndpoint}/${id}`);
    return response.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const getVoucherPaymentPayload = async (
  voucherBatchId: string,
  countryCode: string
): Promise<ApiResponse<{ paymentPayload: PaymentPayload }>> => {
  try {
    const response = await axiosServer.get(
      `${batchEndpoint}/${voucherBatchId}/payment-payload?countryCode=${countryCode}`
    );

    return response.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const getVouchersDetailsByIds = async (
  ids: string[]
): Promise<ApiResponse<{ vouchersDetails: VoucherDetails[] }>> => {
  try {
    const params: Record<string, string[]> = { ids };

    const response = await axiosServer.get(`${voucherEndpoint}/details-all`, {
      params,
    });
    return response.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export type VoucherStatistics = {
  purchased: number;
  active: number;
  redeemed: number;
};

export const getVoucherStatistics = async (): Promise<
  ApiResponse<{ statistics: VoucherStatistics }>
> => {
  try {
    const response = await axiosServer.get(`${voucherEndpoint}/statistics`);
    return response.data;
  } catch (error) {
    return transformToApiError(error);
  }
};

export const createComplimentaryVoucherBatch = async (
  data: CreateComplimentaryVoucherFormData
): Promise<ApiResponse<{ vouchers: VoucherDetails[] }>> => {
  try {
    const postData = {
      ...data,
      validFromUnix: convertDateToUnix(new Date(getStartOfDay(data.validFor))),
      validUntilUnix: convertDateToUnix(new Date(getEndOfDay(data.validFor))),
    };

    const response = await axiosServer.post(
      `${batchEndpoint}/complimentary`,
      postData
    );
    return response.data;
  } catch (error) {
    return transformToApiError(error);
  }
};
